import Image from 'next/image';
import { Link } from 'shared-routing';
import { Routes } from '@common/constants';
import { useTranslations } from '@lang';
import ImgixWrapper from '@common/components/ImgixWrapper';
import { GlobeIcon } from '@common/icons/GlobeIcon';
import { FooterV2Props } from './FooterV2.types';
import {
  StyledFooter,
  FooterTop,
  LogoColumn,
  InfoColumns,
  InfoColumn,
  ColumnTitle,
  InfoList,
  FooterBottom,
  PoweredBy,
  LanguageLabel,
  LogoImage,
  FallbackName,
} from './FooterV2.styles';

export function FooterV2({
  property,
  language,
  termsAndConditionPage,
  privacyAndCookiePolicyPage,
  cancellationPage,
}: FooterV2Props) {
  const { t } = useTranslations();

  const addressLines = property.address
    ? property.address
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean)
    : [];

  return (
    <StyledFooter>
      <FooterTop>
        <LogoColumn>
          {property.logo?.url ? (
            <LogoImage>
              <ImgixWrapper src={property.logo.url} height={60} alt={property.name || ''} />
            </LogoImage>
          ) : (
            <FallbackName>{property.name}</FallbackName>
          )}
        </LogoColumn>

        <InfoColumns>
          <InfoColumn>
            <ColumnTitle>{property.name}</ColumnTitle>
            <InfoList>
              {addressLines.map((line, i) => (
                <li key={i}>{line}</li>
              ))}
              {property.email && (
                <li>
                  <a href={`mailto:${property.email}`}>{property.email}</a>
                </li>
              )}
            </InfoList>
          </InfoColumn>

          {(termsAndConditionPage || privacyAndCookiePolicyPage || cancellationPage) && (
            <InfoColumn>
              <ColumnTitle>{t('homepage.footer.generalInformation')}</ColumnTitle>
              <InfoList>
                {termsAndConditionPage && (
                  <li>
                    <Link route={Routes.TERMS_AND_CONDITIONS}>
                      {t('homepage.termsAndConditions')}
                    </Link>
                  </li>
                )}
                {privacyAndCookiePolicyPage && (
                  <li>
                    <Link route={Routes.PRIVACY_POLICY}>
                      {t('homepage.privacyAndCookiePolicy')}
                    </Link>
                  </li>
                )}
                {cancellationPage && (
                  <li>
                    <Link route={Routes.CANCELLATION_POLICY}>
                      {t('homepage.cancellationPolicy')}
                    </Link>
                  </li>
                )}
              </InfoList>
            </InfoColumn>
          )}
        </InfoColumns>
      </FooterTop>

      <FooterBottom>
        <PoweredBy>
          <span>{t('homepage.footer.poweredBy')}</span>
          <Image
            src="/static/journey-logo.svg"
            alt={t('homepage.footer.journeyLogoAlt' as Parameters<typeof t>[0])}
            width={72}
            height={16}
          />
        </PoweredBy>

        <LanguageLabel>
          <GlobeIcon />
          <span>
            {language ? new Intl.DisplayNames([language], { type: 'language' }).of(language) : ''}
          </span>
        </LanguageLabel>
      </FooterBottom>
    </StyledFooter>
  );
}
